"use client"
import BlogFacebookPostingModal from '@/components/admin/BlogFacebookPostingModal';
import { useRBAC } from '@/components/admin/RBACGuard';
import InstagramActionButtons from '@/components/admin/instagram/InstagramActionButtons';
import InstagramDeleteDialog from '@/components/admin/instagram/InstagramDeleteDialog';
import InstagramPostModal, { type InstagramPostDetails } from '@/components/admin/instagram/InstagramPostModal';
import InstagramPostingModal from '@/components/admin/instagram/InstagramPostingModal';
import InstagramStatusBadge from '@/components/admin/instagram/InstagramStatusBadge';
import SocialMediaEditModal from '@/components/admin/socialmediamanagement/SocialMediaEditModal';
import SocialMediaViewModal, { type SocialPostRow } from '@/components/admin/socialmediamanagement/SocialMediaViewModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import GlassmorphicSelect from '@/components/ui/GlassmorphicSelect';
import { SearchBar } from '@/components/ui/SearchBar';
import { ToastContainer, useToast } from '@/components/ui/ToastContainer';
import * as Avatar from '@radix-ui/react-avatar';
import * as Checkbox from '@radix-ui/react-checkbox';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  Text
} from '@radix-ui/themes';
import {
  Calendar,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileQuestion,
  FileText,
  Filter,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  User,
  UserCheck,
  Share2,
  Instagram
} from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ADMIN_LIST_FILTERS_DEBOUNCE_MS } from '@/lib/constants/adminListUi';
import { resolveBlogFacebookPublishDateTimeState } from '@/lib/social-media/facebook-schedule-fields';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BLOG_LIST_PAGE_SIZE = 5;

function formatLastModified(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) > 1 ? 's' : ''} ago`;
  } else if (diffInDays < 7) {
    return `${Math.floor(diffInDays)} day${Math.floor(diffInDays) > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

function formatFacebookPublishDateTime(date: Date | null) {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Blog {
  _id: string;
  title: string;
  slug: string;
  author: string;
  editor?: string;
  status: 'Draft' | 'Published' | 'Scheduled';
  publishDate?: string;
  tags: string[];
  featuredImage?: string;
  views: number;
  lastModified?: string;
  createdAt: string;
  updatedAt: string;
  blogType: 'Post' | 'News' | 'Thought & Leadership';
  indexed?: boolean;
  // Immutable origin role for the underlying post (set on creation)
  createdByRole?: 'Author' | 'Editor' | 'Admin';
  facebookBlogAutoPostId?: string;
  facebookBlogAutoPostError?: string | null;
  socialMediaLinkedPostId?: string;
  socialMediaPostingEnabled?: boolean;
  socialMediaPostingScheduledAt?: string | null;
  socialMediaPostingPublishedAt?: string | null;
  instagramPostingEnabled?: boolean;
  instagramPublishType?: 'now' | 'scheduled';
  instagramPostingCaption?: string;
  instagramPostingTags?: string[];
  instagramPostingUseFeaturedImage?: boolean;
  instagramPostingImageUrl?: string;
  instagramPostingScheduledAt?: string | null;
  instagramPostingPublishedAt?: string | null;
  instagramPostingStatus?: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed' | 'deleted';
  instagramPostingError?: string | null;
  instagramMediaId?: string;
  instagramCreationId?: string;
  instagramPermalink?: string;
  instagramUsername?: string;
  instagramMediaType?: string;
  instagramLikeCount?: number;
  instagramCommentCount?: number;
  instagramLastSyncAt?: string | null;
  instagramDeletedAt?: string | null;
}

type BlogFacebookLiveStatus =
  | 'none'
  | 'scheduled'
  | 'failed'
  | 'posted'
  | 'live'
  | 'removed'
  | 'token_down';

interface BlogFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedAuthor: string;
  selectedDateRange: string;
  dateFrom?: string;
  dateTo?: string;
}

interface BlogListProps {
  filters: BlogFilters;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  authorFilter: string;
  onAuthorFilterChange: (value: string) => void;
  dateRangeFilter: string;
  onDateRangeFilterChange: (value: string) => void;
  authors: string[];
}

interface BlogResponse {
  blogs: Blog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const statusConfig = {
  Published: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  Draft: { color: 'bg-yellow-100 text-yellow-800', icon: FileText },
  Scheduled: { color: 'bg-blue-100 text-blue-800', icon: Clock }
};

export default function BlogList({
  filters,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  authorFilter,
  onAuthorFilterChange,
  dateRangeFilter,
  onDateRangeFilterChange,
  authors
}: BlogListProps) {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBlogs, setSelectedBlogs] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [_hasFiltersApplied, setHasFiltersApplied] = useState(false);
  const [faqPresenceBySlug, setFaqPresenceBySlug] = useState<Record<string, 'yes' | 'no' | 'loading' | 'error'>>({});
  const [fbLiveByBlogId, setFbLiveByBlogId] = useState<Record<string, BlogFacebookLiveStatus | 'loading'>>({});
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statusStats, setStatusStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    scheduled: 0,
    views: 0
  });
  const [deleting, setDeleting] = useState<string[]>([]);
  const { isAdmin, role: userRole } = useRBAC();
  const normalizedUserRole = (userRole || '').trim().toLowerCase();
  const isAuthorRole = normalizedUserRole === 'author';
  const isEditorRole = normalizedUserRole === 'editor';
  const { showToast, removeToast, toasts } = useToast();
  const [fbComposeBlog, setFbComposeBlog] = useState<Blog | null>(null);
  const [smViewPost, setSmViewPost] = useState<SocialPostRow | null>(null);
  const [smViewOpen, setSmViewOpen] = useState(false);
  const [smEditPost, setSmEditPost] = useState<SocialPostRow | null>(null);
  const [smEditOpen, setSmEditOpen] = useState(false);
  const [smUnlinkBlogId, setSmUnlinkBlogId] = useState<string | null>(null);
  const [smDeleteTarget, setSmDeleteTarget] = useState<{ row: SocialPostRow; unlinkBlogId: string } | null>(null);
  const [smDeleteLoading, setSmDeleteLoading] = useState(false);
  const [igViewPost, setIgViewPost] = useState<InstagramPostDetails | null>(null);
  const [igViewOpen, setIgViewOpen] = useState(false);
  const [igComposeBlog, setIgComposeBlog] = useState<Blog | null>(null);
  const [igComposeOpen, setIgComposeOpen] = useState(false);
  const [igDeleteTarget, setIgDeleteTarget] = useState<Blog | null>(null);
  const [igDeleteLoading, setIgDeleteLoading] = useState(false);
  const [igDiscardTarget, setIgDiscardTarget] = useState<Blog | null>(null);
  const [igDiscardLoading, setIgDiscardLoading] = useState(false);
  const [workflowStatuses, setWorkflowStatuses] = useState<Record<string, {
    status:
    | 'Admin Review Pending'
    | 'Author: Working'
    | 'Editor: Working'
    | 'Author: Reworking'
    | 'Editor: Reworking'
    | 'Completed';
    workflowStatus: string;
    requesterRole: 'Editor' | 'Author';
    requesterId?: string | null;
    assignedEditorId?: string | null;
    editorAccepted?: boolean;
  }>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmData, setConfirmData] = useState<{
    blog: Blog | null;
    nextIndexed: boolean;
  }>({ blog: null, nextIndexed: true });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    blogId: string;
    blogName: string;
  } | null>(null);
  
  // Refs to store the latest functions to avoid re-registering event listeners
  const fetchBlogsRef = useRef<() => Promise<void>>(null);
  const fetchStatusCountsRef = useRef<() => Promise<void>>(null);

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [filters]);
  const debouncedFilters = useDebouncedValue(memoizedFilters, ADMIN_LIST_FILTERS_DEBOUNCE_MS, {
    onDebouncedCommit: () => setCurrentPage(1),
  });

  // Helper function to truncate title to first 5 words
  const truncateTitle = (title: string, maxWords = 5): string => {
    const words = title.trim().split(/\s+/);
    if (words.length <= maxWords) {
      return title;
    }
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Check if any filters are applied (aligned with last API query = debounced)
  const isFiltering = useMemo(() => {
    return (
      debouncedFilters.searchTerm.trim() !== '' ||
      debouncedFilters.selectedStatus !== 'All' ||
      debouncedFilters.selectedAuthor !== 'All' ||
      debouncedFilters.selectedDateRange !== 'All Time' ||
      debouncedFilters.dateFrom !== undefined ||
      debouncedFilters.dateTo !== undefined
    );
  }, [debouncedFilters]);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters from filters
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: String(BLOG_LIST_PAGE_SIZE),
      });

      // Add filter parameters (debounced — API runs after user pauses typing / changing filters)
      if (debouncedFilters.searchTerm) {
        queryParams.append('search', debouncedFilters.searchTerm);
      }
      if (debouncedFilters.selectedStatus !== 'All') {
        queryParams.append('status', debouncedFilters.selectedStatus);
      }
      if (debouncedFilters.selectedAuthor !== 'All') {
        queryParams.append('author', debouncedFilters.selectedAuthor);
      }
      if (debouncedFilters.selectedDateRange !== 'All Time') {
        queryParams.append('dateRange', debouncedFilters.selectedDateRange);
      }
      if (debouncedFilters.dateFrom) {
        queryParams.append('dateFrom', debouncedFilters.dateFrom);
      }
      if (debouncedFilters.dateTo) {
        queryParams.append('dateTo', debouncedFilters.dateTo);
      }

      const response = await fetch(`/api/admin/blogs?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blogs');
      }
      const data: BlogResponse = await response.json();

      // Add lastModified field for display
      const blogsWithLastModified = data.blogs.map(blog => ({
        ...blog,
        lastModified: formatLastModified(blog.updatedAt)
      }));

      setBlogs(blogsWithLastModified);
      setTotalBlogs(data.pagination.total);
      setTotalPages(data.pagination.pages);
      setHasFiltersApplied(isFiltering);

      // Fetch workflow statuses for these blogs
      if (blogsWithLastModified.length > 0) {
        try {
          const workflowRes = await fetch('/api/admin/posts/workflow/batch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              postIds: blogsWithLastModified.map(b => b._id),
              postType: 'blog'
            })
          });
          if (workflowRes.ok) {
            const workflowData = await workflowRes.json();
            if (workflowData.success && workflowData.statusMap) {
              setWorkflowStatuses(workflowData.statusMap);
            }
          }
        } catch (err) {
          console.warn('Failed to fetch workflow statuses:', err);
        }

        const postBlogIds = blogsWithLastModified
          .filter((b) => (b.blogType || 'Post') === 'Post')
          .map((b) => b._id);
        if (postBlogIds.length > 0) {
          setFbLiveByBlogId(
            Object.fromEntries(postBlogIds.map((id) => [id, 'loading' as const]))
          );
          try {
            const fbRes = await fetch('/api/admin/blogs/facebook-status/batch', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ blogIds: postBlogIds }),
              cache: 'no-store',
            });
            const fbData = (await fbRes.json()) as {
              success?: boolean;
              statuses?: Record<string, BlogFacebookLiveStatus>;
            };
            if (fbRes.ok && fbData.success && fbData.statuses) {
              setFbLiveByBlogId(fbData.statuses);
            } else {
              setFbLiveByBlogId({});
            }
          } catch {
            setFbLiveByBlogId({});
          }

          try {
            const igRes = await fetch('/api/admin/blogs/instagram-status/batch', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ blogIds: postBlogIds }),
              cache: 'no-store',
            });
            const igData = (await igRes.json()) as {
              success?: boolean;
              updates?: Record<string, Partial<Blog>>;
            };
            if (igRes.ok && igData.success && igData.updates) {
              setBlogs((prev) =>
                prev.map((blog) => {
                  const patch = igData.updates?.[blog._id];
                  if (!patch) return blog;
                  const next = { ...blog, ...patch };
                  return next.updatedAt
                    ? { ...next, lastModified: formatLastModified(next.updatedAt) }
                    : next;
                })
              );
            }
          } catch (err) {
            console.warn('Failed to refresh blog Instagram live statuses:', err);
          }
        } else {
          setFbLiveByBlogId({});
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch blogs');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, isFiltering, currentPage]);

  const fetchSocialRowForBlog = useCallback(async (blog: Blog): Promise<SocialPostRow | null> => {
    if (blog.socialMediaLinkedPostId) {
      const r = await fetch(`/api/admin/social-media/posts/${blog.socialMediaLinkedPostId}`);
      const d = (await r.json()) as { success?: boolean; post?: SocialPostRow };
      if (r.ok && d.success && d.post) return d.post;
    }
    if (blog.facebookBlogAutoPostId) {
      const r = await fetch(
        `/api/admin/social-media/posts/lookup?postId=${encodeURIComponent(blog.facebookBlogAutoPostId)}`
      );
      const d = (await r.json()) as { success?: boolean; post?: SocialPostRow | null };
      if (r.ok && d.success && d.post) return d.post;
    }
    return null;
  }, []);

  const openBlogFacebookSocial = useCallback(
    async (blog: Blog) => {
      if ((blog.blogType || 'Post') !== 'Post') return;
      try {
        const row = await fetchSocialRowForBlog(blog);
        if (row) {
          setSmUnlinkBlogId(blog._id);
          setSmViewPost(row);
          setSmViewOpen(true);
          return;
        }
        if (blog.facebookBlogAutoPostError?.trim() && !blog.facebookBlogAutoPostId?.trim() && !blog.socialMediaLinkedPostId?.trim()) {
          setFbComposeBlog(blog);
          return;
        }

        const hasLinkHint = !!(
          blog.facebookBlogAutoPostId?.trim() || blog.socialMediaLinkedPostId?.trim()
        );
        if (hasLinkHint) {
          const rid = encodeURIComponent(String(blog._id).trim());
          const r = await fetch(`/api/admin/blogs/${rid}/reconcile-facebook-link`, {
            method: 'POST',
            cache: 'no-store',
          });
          const d = (await r.json()) as {
            success?: boolean;
            error?: string;
            cleared?: boolean;
            post?: SocialPostRow;
          };
          if (!r.ok || !d.success) {
            showToast(d.error || 'Could not fix Facebook link on this blog.', 'error');
            return;
          }
          if (d.post) {
            setSmUnlinkBlogId(blog._id);
            setSmViewPost(d.post);
            setSmViewOpen(true);
            await fetchBlogs();
            return;
          }
          await fetchBlogs();
          if (d.cleared) {
            showToast('Removed an out-of-date Facebook link. You can schedule a new post.', 'info');
          }
          setFbComposeBlog({
            ...blog,
            facebookBlogAutoPostId: '',
            socialMediaLinkedPostId: '',
            facebookBlogAutoPostError: null,
          });
          return;
        }
        setFbComposeBlog(blog);
      } catch {
        showToast('Failed to load Facebook post', 'error');
      }
    },
    [fetchSocialRowForBlog, showToast, fetchBlogs]
  );

  const handleFbPosted = useCallback(
    async (info: { linkedPostId?: string | null; postId?: string; blogId: string }) => {
      await fetchBlogs();
      if (info.linkedPostId) {
        const r = await fetch(`/api/admin/social-media/posts/${info.linkedPostId}`);
        const d = (await r.json()) as { success?: boolean; post?: SocialPostRow };
        if (r.ok && d.success && d.post) {
          setSmUnlinkBlogId(info.blogId);
          setSmViewPost(d.post);
          setSmViewOpen(true);
        }
      }
    },
    [fetchBlogs]
  );

  const confirmSmDelete = useCallback(async () => {
    if (!smDeleteTarget) return;
    setSmDeleteLoading(true);
    try {
      const q = new URLSearchParams({ unlinkBlogId: smDeleteTarget.unlinkBlogId });
      const r = await fetch(`/api/admin/social-media/posts/${smDeleteTarget.row._id}?${q}`, { method: 'DELETE' });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) throw new Error(d.error || 'Delete failed');
      showToast('Facebook post removed.', 'success');
      setSmDeleteTarget(null);
      setSmViewOpen(false);
      setSmViewPost(null);
      setSmUnlinkBlogId(null);
      await fetchBlogs();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setSmDeleteLoading(false);
    }
  }, [smDeleteTarget, showToast, fetchBlogs]);

  const instagramDetailsForBlog = useCallback((blog: Blog): InstagramPostDetails => {
    return {
      title: blog.title,
      imageUrl: blog.instagramPostingImageUrl || blog.featuredImage || null,
      caption: blog.instagramPostingCaption || '',
      status: blog.instagramPostingStatus || 'draft',
      publishedAt: blog.instagramPostingPublishedAt || null,
      scheduledAt: blog.instagramPostingScheduledAt || null,
      mediaId: blog.instagramMediaId || null,
      username: blog.instagramUsername || null,
      permalink: blog.instagramPermalink || null,
      mediaType: blog.instagramMediaType || 'IMAGE',
      likeCount: blog.instagramLikeCount ?? 0,
      commentCount: blog.instagramCommentCount ?? 0,
      lastSyncAt: blog.instagramLastSyncAt || null,
    };
  }, []);

  const openInstagramPost = useCallback(
    (blog: Blog) => {
      if (!blog.instagramMediaId?.trim()) {
        showToast('No Instagram post is linked to this blog yet.', 'info');
        return;
      }
      setIgViewPost(instagramDetailsForBlog(blog));
      setIgViewOpen(true);
    },
    [instagramDetailsForBlog, showToast]
  );

  const confirmInstagramDelete = useCallback(async () => {
    if (!igDeleteTarget) return;
    setIgDeleteLoading(true);
    try {
      const r = await fetch(`/api/admin/blogs/${igDeleteTarget._id}/instagram-post`, {
        method: 'DELETE',
        cache: 'no-store',
      });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) throw new Error(d.error || 'Instagram delete failed');
      showToast('Instagram post deleted.', 'success');
      setIgDeleteTarget(null);
      setIgViewOpen(false);
      setIgViewPost(null);
      await fetchBlogs();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Instagram delete failed', 'error');
    } finally {
      setIgDeleteLoading(false);
    }
  }, [igDeleteTarget, showToast, fetchBlogs]);

  const confirmInstagramDiscard = useCallback(async () => {
    if (!igDiscardTarget) return;
    setIgDiscardLoading(true);
    try {
      const r = await fetch(`/api/admin/blogs/${igDiscardTarget._id}/instagram-post`, {
        method: 'PATCH',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instagramPostingEnabled: false }),
      });
      const d = (await r.json()) as { success?: boolean; error?: string };
      if (!r.ok || !d.success) throw new Error(d.error || 'Instagram discard failed');

      const clearedBlog: Blog = {
        ...igDiscardTarget,
        instagramPostingEnabled: false,
        instagramPostingStatus: 'draft',
        instagramPostingError: null,
        instagramPostingScheduledAt: null,
        instagramPostingPublishedAt: null,
        instagramMediaId: '',
        instagramCreationId: '',
        instagramPermalink: '',
        instagramUsername: '',
        instagramMediaType: '',
        instagramDeletedAt: null,
      };

      showToast('Failed Instagram post discarded. You can create a new post now.', 'success');
      setIgDiscardTarget(null);
      setIgViewOpen(false);
      setIgViewPost(null);
      await fetchBlogs();
      setIgComposeBlog(clearedBlog);
      setIgComposeOpen(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Instagram discard failed', 'error');
    } finally {
      setIgDiscardLoading(false);
    }
  }, [igDiscardTarget, showToast, fetchBlogs]);

  const openInstagramCompose = useCallback((blog: Blog) => {
    if (blog.instagramMediaId?.trim() && !blog.instagramDeletedAt) {
      showToast('This blog already has an Instagram post.', 'info');
      return;
    }
    setIgComposeBlog(blog);
    setIgComposeOpen(true);
  }, [showToast]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      setStatsError(null);
      setStatsLoading(true);

      const response = await fetch('/api/admin/blogs/stats');
      if (!response.ok) {
        throw new Error('Failed to fetch blog stats');
      }

      const data = await response.json();
      if (!data?.success || !data?.stats) {
        throw new Error(data?.error || 'Failed to fetch blog stats');
      }

      const getValue = (key: string) => {
        const rawValue = data.stats?.[key]?.value;
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : 0;
      };

      setStatusStats({
        total: getValue('totalBlogs'),
        published: getValue('publishedBlogs'),
        draft: getValue('draftBlogs'),
        scheduled: getValue('scheduledBlogs'),
        views: getValue('totalViews')
      });
    } catch (err) {
      console.error('Failed to fetch blog stats', err);
      setStatsError('Unable to load blog stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Update refs whenever functions change
  useEffect(() => {
    fetchBlogsRef.current = fetchBlogs;
  }, [fetchBlogs]);

  useEffect(() => {
    fetchStatusCountsRef.current = fetchStatusCounts;
  }, [fetchStatusCounts]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void fetchBlogsRef.current?.();
      void fetchStatusCountsRef.current?.();
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  // Fetch blogs when debounced filters or page changes (server-side pagination — one page per request)
  useEffect(() => {
    fetchBlogs();
  }, [currentPage, debouncedFilters, fetchBlogs]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  // Listen for post-related notifications to auto-reload blogs
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePostNotification = (_event: CustomEvent) => {
      // Reload blogs when a post-related notification is received
      if (fetchBlogsRef.current) {
        fetchBlogsRef.current();
      }
      if (fetchStatusCountsRef.current) {
        fetchStatusCountsRef.current();
      }
    };

    window.addEventListener('postNotificationReceived', handlePostNotification as EventListener);

    return () => {
      window.removeEventListener('postNotificationReceived', handlePostNotification as EventListener);
    };
  }, []); // Empty dependency array - listener is registered once and uses refs

  // For current page blogs, check if an FAQ exists for each slug (list is already server-filtered)
  useEffect(() => {
    const checkFaqs = async () => {
      if (!blogs || blogs.length === 0) return;
      const slugs = blogs.map(b => b.slug);
      setFaqPresenceBySlug(prev => {
        const draft: Record<string, 'yes' | 'no' | 'loading' | 'error'> = { ...prev };
        slugs.forEach(slug => {
          if (!(slug in draft)) draft[slug] = 'loading';
        });
        return draft;
      });

      await Promise.all(slugs.map(async (slug) => {
        try {
          // Use the check-presence endpoint which doesn't apply role-based filtering
          // This ensures editors/authors can see if FAQs exist, even if they didn't create them
          const resp = await fetch(`/api/admin/faqs/check-presence?blogSlug=${encodeURIComponent(slug)}`);
          if (!resp.ok) throw new Error('fail');
          const data = await resp.json();
          const hasFAQs = data?.hasFAQs ?? false;
          setFaqPresenceBySlug(prev => ({ ...prev, [slug]: hasFAQs ? 'yes' : 'no' }));
        } catch {
          setFaqPresenceBySlug(prev => ({ ...prev, [slug]: 'error' }));
        }
      }));
    };
    checkFaqs();
  }, [blogs]);

  const handleSelectAll = () => {
    if (selectedBlogs.length === blogs.length && blogs.length > 0) {
      setSelectedBlogs([]);
    } else {
      setSelectedBlogs(blogs.map(blog => blog._id));
    }
  };

  const handleSelectBlog = (blogId: string) => {
    if (selectedBlogs.includes(blogId)) {
      setSelectedBlogs(selectedBlogs.filter(id => id !== blogId));
    } else {
      setSelectedBlogs([...selectedBlogs, blogId]);
    }
  };

  const handleBulkAction = async (action: 'publish' | 'unpublish' | 'delete') => {
    if (selectedBlogs.length === 0) return;

    try {
      const response = await fetch('/api/admin/blogs/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          blogIds: selectedBlogs
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to perform bulk action');
      }

      // Refresh the blog list
      await fetchBlogs();
      setSelectedBlogs([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    }
  };

  const handleDeleteBlog = (blogId: string) => {
    const blog = blogs.find(b => b._id === blogId);
    setConfirmDialog({
      open: true,
      blogId,
      blogName: blog ? `${blog.title} - ${blog.author}` : 'this blog'
    });
  };

  const confirmDelete = async () => {
    if (!confirmDialog) return;

    const blogId = confirmDialog.blogId;
    setDeleting(prev => [...prev, blogId]);

    try {
      const response = await fetch(`/api/admin/blogs/${blogId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete blog' }));
        throw new Error(errorData.error || 'Failed to delete blog');
      }

      // Refresh the blog list
      await fetchBlogs();
      setConfirmDialog(null);

      // Dispatch events for cross-page refresh
      window.dispatchEvent(new CustomEvent('blog-refresh'));
      window.dispatchEvent(new CustomEvent('trash-refresh'));

      // Show success message
      showToast(`Blog "${confirmDialog.blogName}" moved to trash successfully!`, 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete blog';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      // Remove from deleting state
      setDeleting(prev => prev.filter(id => id !== blogId));
    }
  };

  const toggleIndex = async (blogId: string, nextIndexed: boolean) => {
    try {
      setConfirmLoading(true);
      const response = await fetch(`/api/admin/blogs/${blogId}/index`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ indexed: nextIndexed })
      });
      if (!response.ok) throw new Error('Failed to update index status');
      await fetchBlogs();
      setConfirmOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update index status');
    } finally {
      setConfirmLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.Draft;
  };

  // Determine who originally created the blog for display badge
  const getCreatorBadge = (blog: Blog) => {
    // Primary source of truth: immutable origin field on the blog document.
    // This is set only when the post is created and never updated afterwards.
    if (blog.createdByRole) {
      const role = blog.createdByRole;
      if (role === 'Author') {
        return { label: 'Author Created' as const, color: 'blue' as const };
      }
      if (role === 'Editor') {
        return { label: 'Editor Created' as const, color: 'purple' as const };
      }
      // Default fallback for any other role is Admin.
      return { label: 'Admin Created' as const, color: 'gray' as const };
    }

    // Fallback for legacy posts that don't have createdByRole yet.
    // In this case we approximate origin from workflow requester role,
    // but this will never override a stored origin.
    const workflowStatus = workflowStatuses[blog._id];

    // Default assumption: blogs managed directly in admin panel are created by Admin
    let label: 'Author Created' | 'Editor Created' | 'Admin Created' = 'Admin Created';
    let color: 'blue' | 'purple' | 'gray' = 'gray';

    if (workflowStatus) {
      if (workflowStatus.requesterRole === 'Author') {
        // Covers both:
        // - Author created the post and requested publish
        // - Author created the post and Admin later assigned an editor
        label = 'Author Created';
        color = 'blue';
      } else if (workflowStatus.requesterRole === 'Editor') {
        // Editor initiated the workflow.
        // If editor also chose themselves as author, count as Editor Created.
        if (blog.author && blog.editor && blog.author === blog.editor) {
          label = 'Editor Created';
          color = 'purple';
        } else {
          // Fallback: editor-driven content
          label = 'Editor Created';
          color = 'purple';
        }
      }
    }

    return { label, color };
  };

  const tableBusy = loading;

  const statCards = [
    {
      key: 'total',
      label: 'Total Blogs',
      value: statusStats.total,
      icon: FileText,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'bg-blue-500',
      showLoader: true
    },
    {
      key: 'published',
      label: 'Published Posts',
      value: statusStats.published,
      icon: CheckCircle,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'bg-green-500',
      showLoader: true
    },
    {
      key: 'draft',
      label: 'Drafts',
      value: statusStats.draft,
      icon: Pencil,
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      borderColor: 'bg-yellow-500',
      showLoader: true
    },
    {
      key: 'scheduled',
      label: 'Scheduled',
      value: statusStats.scheduled,
      icon: Clock,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'bg-purple-500',
      showLoader: true
    },
    {
      key: 'views',
      label: 'Total Views',
      value: statusStats.views,
      icon: Eye,
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      borderColor: 'bg-indigo-500',
      showLoader: true
    }
  ];

  return (
    <Box className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
      {/* Header + Stats */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100">
        <div className="px-6 py-6 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3">
                <div className="h-1 w-1 rounded-full bg-blue-500 animate-pulse" />
                <h2 className="text-xl  text-gray-900 tracking-tight">
                  Manage <span className="text-blue-600">Blogs</span>
                </h2>
              </div>
              <p className="text-gray-600 text-sm ml-4">
                Monitor every post, status, and publication timeline from a single place.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="soft" color="gray" className="!px-4 !py-3 !rounded-md">
                Total Results: {totalBlogs.toLocaleString()}
              </Badge>
              <Link href="/admin/blogs/new" className="inline-flex">
                <div className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-white px-2 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                    <Plus className="h-3.5 w-3.5" />
                  </div>
                  <span>Create Blog</span>
                </div>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {statCards.map((card) => {
              const Icon = card.icon;
              const displayValue = card.showLoader === false
                ? card.value.toLocaleString()
                : statsLoading
                  ? '--'
                  : card.value.toLocaleString();

              return (
                <div
                  key={card.key}
                  className="group rounded-md border border-gray-200 p-4 hover:shadow-lg hover:border-gray-300 transition-all duration-300 relative overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                  }}
                >
                  {/* Left border - 2/3 height, centered */}
                  <div
                    className={`absolute left-0 top-1/2 -translate-y-1/2 ${card.borderColor}`}
                    style={{
                      width: '2px',
                      height: '66.67%',
                      borderRadius: '0 2px 2px 0',
                    }}
                  />

                  <div className="relative z-10 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{card.label}</p>
                      <p className="text-2xl font-bold text-gray-900">{displayValue}</p>
                    </div>
                    <div className={`flex h-8 w-8 items-center justify-center rounded-sm ${card.iconBg} ${card.iconColor} shadow-sm transition-colors group-hover:scale-105 border border-gray-200 p-1`}>
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>
                  {statsError && (
                    <p className="text-[11px] text-red-500 mt-2">Stats unavailable</p>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </div>

      <div className="p-6 bg-white">
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          {/* Search & Filters */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-4 flex-wrap justify-between">
              <div className="w-1/2 min-w-[160px]">
                <SearchBar
                  value={searchTerm}
                  onChange={onSearchChange}
                  placeholder="Search by title, author, or content..."
                />
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <GlassmorphicSelect
                    value={statusFilter}
                    onChange={onStatusFilterChange}
                    options={[
                      { value: "All", label: "All Status" },
                      { value: "Published", label: "Published" },
                      { value: "Draft", label: "Draft" },
                      { value: "Scheduled", label: "Scheduled" },
                    ]}
                    className="min-w-[140px]"
                  />
                </div>

                <GlassmorphicSelect
                  value={authorFilter}
                  onChange={onAuthorFilterChange}
                  options={authors.map(author => ({ value: author, label: author }))}
                  placeholder="All Authors"
                  className="min-w-[140px]"
                />

                <GlassmorphicSelect
                  value={dateRangeFilter}
                  onChange={onDateRangeFilterChange}
                  options={[
                    { value: "All Time", label: "All Time" },
                    { value: "Today", label: "Today" },
                    { value: "This Week", label: "This Week" },
                    { value: "This Month", label: "This Month" },
                    { value: "Last 3 Months", label: "Last 3 Months" },
                  ]}
                  className="min-w-[140px]"
                />
              </div>
            </div>
          </div>

          <div className="px-5 py-5 space-y-5">
            {/* Bulk Actions */}
            {selectedBlogs.length > 0 && (
              <Box className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-md">
                <Flex justify="between" align="center">
                  <Text size="2" className="text-blue-700">
                    {selectedBlogs.length} blog{selectedBlogs.length > 1 ? 's' : ''} selected
                  </Text>
                  <Flex gap="2">
                    <Button size="1" color="green" onClick={() => handleBulkAction('publish')}>
                      Publish
                    </Button>
                    <Button size="1" color="yellow" onClick={() => handleBulkAction('unpublish')}>
                      Unpublish
                    </Button>
                    <Button size="1" color="red" onClick={() => handleBulkAction('delete')}>
                      Delete
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            )}

            {error && (
              <Box className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <Flex align="center" justify="between" gap="3" wrap="wrap">
                  <Text size="2" className="text-red-800">
                    {error}
                  </Text>
                  <Button size="1" variant="soft" color="red" onClick={() => void fetchBlogs()}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Try again
                  </Button>
                </Flex>
              </Box>
            )}

            {/* Blogs Table — loader only over the list + pagination */}
            <div className="relative min-h-[240px] rounded-lg border border-gray-100">
              {tableBusy && (
                <div
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/75 backdrop-blur-[1px]"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <Text size="2" className="text-gray-600">
                    {isFiltering ? 'Updating results…' : 'Loading blogs…'}
                  </Text>
                </div>
              )}
              <Table.Root>
              <Table.Header>
                <Table.Row className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-t-lg">
                  <Table.ColumnHeaderCell className="bg-transparent rounded-tl-lg">
                    <Checkbox.Root
                      checked={selectedBlogs.length === blogs.length && blogs.length > 0}
                      onCheckedChange={handleSelectAll}
                      className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                      <Checkbox.Indicator>
                        <Check className="h-3 w-3 text-white" />
                      </Checkbox.Indicator>
                    </Checkbox.Root>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Blog Post</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Author</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Editor</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Status</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Publish Date</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Index</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">FAQ</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Facebook</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Publish Date & Time</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600 inline-flex items-center gap-1">
                      <Instagram className="h-3.5 w-3.5 text-pink-600" />
                      Instagram
                    </Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">IG Publish Date & Time</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">IG Actions</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent">
                    <Text weight="medium" size="2" className="text-gray-600">Views</Text>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="bg-transparent rounded-tr-lg">
                    <Text weight="medium" size="2" className="text-gray-600">Actions</Text>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {blogs.map((blog) => {
                  const status = getStatusConfig(blog.status);
                  const StatusIcon = status.icon;
                  const workflowStatus = workflowStatuses[blog._id];

                  // Determine row color based on workflow status
                  let rowBorderStyle: React.CSSProperties = {};
                  let workflowStatusDisplay = null;
                  if (workflowStatus) {
                    const isApprovalPending = workflowStatus.status === 'Admin Review Pending';
                    const isWorking =
                      workflowStatus.status === 'Author: Working' ||
                      workflowStatus.status === 'Editor: Working' ||
                      workflowStatus.status === 'Author: Reworking' ||
                      workflowStatus.status === 'Editor: Reworking';
                    const isCompleted = workflowStatus.status === 'Completed';

                    if (isApprovalPending) {
                      rowBorderStyle = { borderLeftWidth: '4px', borderLeftStyle: 'solid', borderLeftColor: '#059669' }; // emerald-600
                      workflowStatusDisplay = (
                        <Flex direction="column" gap="2" className="ml-2">
                          {/* Row 1: Requester - Cyan */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#cffafe', color: '#164e63' }}>
                            <User className="h-3 w-3 mr-1" />
                            <span className="font-semibold">{workflowStatus.requesterRole}</span>
                            <span className="mx-1">→</span>
                            <span>
                              {workflowStatus.workflowStatus === 'rework_requested'
                                ? 'Rework Request'
                                : workflowStatus.workflowStatus === 'rejected' || workflowStatus.workflowStatus === 'cancelled'
                                  ? 'Reject Request'
                                  : workflowStatus.requesterRole === 'Author'
                                    ? 'Review & Assign Editor Request'
                                    : 'Review & Publish Request'}
                            </span>
                          </Badge>
                          {/* Row 2: Approver - Pink */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#fce7f3', color: '#831843' }}>
                            <UserCheck className="h-3 w-3 mr-1" />
                            <span className="font-semibold">Admin</span>
                            <span className="mx-1">→</span>
                            <span>Approval Pending</span>
                          </Badge>
                          {/* Row 3: Final Status - Purple */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#f3e8ff', color: '#581c87' }}>
                            <Clock className="h-3 w-3 mr-1" />
                            Current Status: Admin Review Pending
                          </Badge>
                        </Flex>
                      );
                    } else if (isWorking) {
                      rowBorderStyle = { borderLeftWidth: '4px', borderLeftStyle: 'solid', borderLeftColor: '#6366f1' }; // indigo-500
                      const workingRole =
                        workflowStatus.status === 'Author: Working' ||
                          workflowStatus.status === 'Author: Reworking'
                          ? 'Author'
                          : 'Editor';
                      // Determine approver status based on workflowStatus
                      let approverText = workingRole;
                      let approverStatus = workingRole === 'Editor' ? 'Working on post' : 'Working';

                      if (workflowStatus.workflowStatus === 'editor_assigned_pending_review') {
                        approverText = 'Editor';
                        approverStatus = 'Working on post';
                      } else if (workflowStatus.workflowStatus === 'rework_requested') {
                        if (workflowStatus.assignedEditorId) {
                          approverText = 'Editor';
                          approverStatus = 'Rework';
                        } else {
                          approverText = workflowStatus.requesterRole === 'Editor' ? 'Editor' : 'Author';
                          approverStatus = 'Rework';
                        }
                      } else if (workflowStatus.workflowStatus === 'rejected' || workflowStatus.workflowStatus === 'cancelled') {
                        approverText = workflowStatus.requesterRole === 'Editor' ? 'Editor' : 'Author';
                        approverStatus = workflowStatus.workflowStatus === 'rejected' ? 'Rejected' : 'Cancelled';
                      }

                      workflowStatusDisplay = (
                        <Flex direction="column" gap="2" className="ml-2">
                          {/* Row 1: Requester - Cyan */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#cffafe', color: '#164e63' }}>
                            <User className="h-3 w-3 mr-1" />
                            <span className="font-semibold">{workflowStatus.requesterRole}</span>
                            <span className="mx-1">→</span>
                            <span>
                              {workflowStatus.workflowStatus === 'rework_requested'
                                ? 'Rework Request'
                                : workflowStatus.workflowStatus === 'rejected' || workflowStatus.workflowStatus === 'cancelled'
                                  ? 'Reject Request'
                                  : workflowStatus.workflowStatus === 'editor_assigned_pending_review' && workflowStatus.requesterRole === 'Author'
                                    ? 'Author Submission Completed'
                                    : workflowStatus.requesterRole === 'Author'
                                      ? 'Review & Assign Editor Request'
                                      : 'Review & Publish Request'}
                            </span>
                          </Badge>
                          {/* Row 2: Approver - Pink */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#fce7f3', color: '#831843' }}>
                            <UserCheck className="h-3 w-3 mr-1" />
                            <span className="font-semibold">{approverText}</span>
                            <span className="mx-1">→</span>
                            <span>{approverStatus}</span>
                          </Badge>
                          {/* Row 3: Final Status - Purple */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#f3e8ff', color: '#581c87' }}>
                            <FileText className="h-3 w-3 mr-1" />
                            {workflowStatus.status === 'Completed'
                              ? 'Current status: Post published'
                              : workflowStatus.status === 'Admin Review Pending'
                                ? 'Current status: Admin review pending'
                                : workflowStatus.status === 'Author: Working'
                                  ? 'Current status: Author working on post'
                                  : workflowStatus.status === 'Editor: Working'
                                    ? 'Current status: Editor working on post'
                                    : workflowStatus.status === 'Author: Reworking'
                                      ? 'Current status: Author reworking post'
                                      : workflowStatus.status === 'Editor: Reworking'
                                        ? 'Current status: Editor reworking post'
                                        : `Current status: ${workflowStatus.status}`}
                          </Badge>
                        </Flex>
                      );
                    } else if (isCompleted) {
                      rowBorderStyle = { borderLeftWidth: '4px', borderLeftStyle: 'solid', borderLeftColor: '#22c55e' }; // green-500
                      workflowStatusDisplay = (
                        <Flex direction="column" gap="2" className="ml-2">
                          {/* Row 1: Requester - Cyan */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#cffafe', color: '#164e63' }}>
                            <User className="h-3 w-3 mr-1" />
                            <span className="font-semibold">{workflowStatus.requesterRole}</span>
                            <span className="mx-1">→</span>
                            <span>
                              {workflowStatus.workflowStatus === 'rework_requested'
                                ? 'Rework Request'
                                : workflowStatus.workflowStatus === 'rejected' || workflowStatus.workflowStatus === 'cancelled'
                                  ? 'Reject Request'
                                  : workflowStatus.requesterRole === 'Author'
                                    ? 'Review & Assign Editor Request'
                                    : 'Review & Publish Request'}
                            </span>
                          </Badge>
                          {/* Row 2: Approver - Pink */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#fce7f3', color: '#831843' }}>
                            <UserCheck className="h-3 w-3 mr-1" />
                            <span className="font-semibold">Admin</span>
                            <span className="mx-1">→</span>
                            <span>Approved</span>
                          </Badge>
                          {/* Row 3: Final Status - Purple */}
                          <Badge variant="soft" className="w-full" style={{ backgroundColor: '#f3e8ff', color: '#581c87' }}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Current Status: Completed
                          </Badge>
                        </Flex>
                      );
                    }
                  }

                  // Simple rule: If status is "Admin Review Pending" AND post is NOT published, disable entire row for Editor
                  // Published posts should always be editable by editors
                  const isPublished = blog.status === 'Published';
                  const isApprovalPending =
                    workflowStatus &&
                    (workflowStatus.status === 'Admin Review Pending' ||
                      workflowStatus.workflowStatus === 'pending_admin_review');
                  // Only disable if approval is pending AND post is not published
                  const rowDisabled = isApprovalPending && isEditorRole && !isPublished;

                  return (
                    <Table.Row
                      key={blog._id}
                      className={`hover:bg-gray-50 !text-gray-600 ${rowDisabled
                        ? 'pointer-events-none opacity-60 bg-emerald-50 cursor-not-allowed'
                        : ''
                        }`}
                      style={{ ...rowBorderStyle, verticalAlign: 'middle' }}
                    >
                      <Table.Cell>
                        <Checkbox.Root
                          checked={selectedBlogs.includes(blog._id)}
                          onCheckedChange={() => handleSelectBlog(blog._id)}
                          disabled={rowDisabled}
                          className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Checkbox.Indicator>
                            <Check className="h-3 w-3 text-white" />
                          </Checkbox.Indicator>
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="3" align="center">
                          <div className="flex flex-col gap-1">
                            <Avatar.Root className="flex-shrink-0 h-16 w-24 rounded-md overflow-hidden">
                              <Avatar.Image
                                className="h-16 w-24 object-cover"
                                src={blog.featuredImage || '/api/placeholder/150/100'}
                                alt={blog.title}
                              />
                              <Avatar.Fallback className="h-16 w-24 bg-gray-200 flex items-center justify-center">
                                <FileText className="h-8 w-8 text-gray-400" />
                              </Avatar.Fallback>
                            </Avatar.Root>
                            {/* Origin badge just below the post image */}
                            {(() => {
                              const creator = getCreatorBadge(blog);
                              const badgeColor =
                                creator.color === 'blue'
                                  ? 'blue'
                                  : creator.color === 'purple'
                                    ? 'purple'
                                    : 'gray';
                              return (
                                <Badge
                                  variant="soft"
                                  color={badgeColor}
                                  className="w-fit text-[11px]"
                                >
                                  {creator.label}
                                </Badge>
                              );
                            })()}
                          </div>
                          <Box >
                            <Text weight="medium" className="text-gray-600 truncate" title={blog.title}>
                              {truncateTitle(blog.title)}
                            </Text>
                            <Flex gap="2" mt="1">
                              {blog.tags.slice(0, 3).map((tag, index) => (
                                <Badge key={index} variant="soft" color="gray">
                                  {tag}
                                </Badge>
                              ))}
                              {blog.tags.length > 3 && (
                                <Badge variant="soft" color="gray">
                                  +{blog.tags.length - 3}
                                </Badge>
                              )}
                            </Flex>
                          </Box>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" align="center">
                          <User className="h-4 w-4 text-gray-400" />
                          <Text size="2" className="text-gray-600">{blog.author || '—'}</Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" align="center">
                          <UserCheck className="h-4 w-4 text-gray-400" />
                          <Text size="2" className="text-gray-600">{blog.editor || blog.author || '—'}</Text>
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          // When post is truly live (admin published), keep status simple.
                          // Otherwise, show full workflow rows for better visibility.
                          const isAdminPublished =
                            blog.status === 'Published' &&
                            workflowStatus &&
                            (
                              workflowStatus.status === 'Completed' ||
                              workflowStatus.workflowStatus === 'approved_published'
                            );

                          return (
                            <Flex direction="column" gap="2">
                              <Badge
                                variant="soft"
                                color={
                                  blog.status === 'Published'
                                    ? 'green'
                                    : blog.status === 'Draft'
                                      ? 'yellow'
                                      : 'blue'
                                }
                              >
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {blog.status}
                              </Badge>
                              {!isAdminPublished && workflowStatusDisplay}
                            </Flex>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        {blog.publishDate ? (
                          <Flex gap="1" align="center">
                            <Calendar className="h-4 w-4" />
                            <Text size="2">
                              {(() => {
                                // Show only date part (YYYY-MM-DD or local date), hide time
                                const raw = blog.publishDate as string;
                                // If it's an ISO string with time, split at 'T'
                                if (raw.includes('T')) {
                                  return raw.split('T')[0];
                                }
                                // Fallback: try Date parsing and format as locale date
                                const d = new Date(raw);
                                if (!isNaN(d.getTime())) {
                                  return d.toLocaleDateString();
                                }
                                // As a last resort, show the raw value
                                return raw;
                              })()}
                            </Text>
                          </Flex>
                        ) : (
                          <Text size="2" className="text-gray-400">Not set</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          // Check if editor can toggle index (for all published posts)
                          // If a post is published, it's considered approved (otherwise it wouldn't be published)
                          const isPublished = blog.status === 'Published';
                          const canEditorToggleIndex = isEditorRole && isPublished;
                          const canToggleIndex = isAdmin() || canEditorToggleIndex;

                          return (
                            <Flex align="center" gap="2">
                              <Checkbox.Root
                                checked={blog.indexed ?? false}
                                onCheckedChange={(checked) => {
                                  if (!canToggleIndex) return;
                                  const next = checked === true;
                                  setConfirmData({ blog, nextIndexed: next });
                                  setConfirmOpen(true);
                                }}
                                disabled={!canToggleIndex}
                                className="relative inline-flex h-5 w-10 items-center rounded-full transition-colors bg-red-200 data-[state=checked]:bg-green-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label={(blog.indexed ?? false) ? 'Indexed' : 'No Index'}
                              >
                                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1 data-[state=checked]:translate-x-5" />
                              </Checkbox.Root>
                              <Badge variant="soft" color={(blog.indexed ?? false) ? 'green' : 'red'}>
                                {(blog.indexed ?? false) ? 'Indexed' : 'No Index'}
                              </Badge>
                            </Flex>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          const state = faqPresenceBySlug[blog.slug] || 'loading';
                          if (state === 'loading') {
                            return (
                              <Badge variant="soft" color="gray">Checking…</Badge>
                            );
                          }
                          if (state === 'error') {
                            return (
                              <Badge variant="soft" color="orange">Unknown</Badge>
                            );
                          }
                          return (
                            <Badge variant="soft" color={state === 'yes' ? 'green' : 'red'}>
                              {state === 'yes' ? 'Yes' : 'No'}
                            </Badge>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          if ((blog.blogType || 'Post') !== 'Post') {
                            return <Text size="2" className="text-gray-400">—</Text>;
                          }
                          const live = fbLiveByBlogId[blog._id];
                          if (live === 'loading') {
                            return <Badge variant="soft" color="gray">Checking…</Badge>;
                          }
                          const status: BlogFacebookLiveStatus =
                            live ??
                            (blog.facebookBlogAutoPostError?.trim()
                              ? 'failed'
                              : blog.facebookBlogAutoPostId?.trim() ||
                                  blog.socialMediaLinkedPostId?.trim()
                                ? 'posted'
                                : 'none');
                          const badge: Record<
                            BlogFacebookLiveStatus,
                            { label: string; color: 'green' | 'red' | 'gray' | 'blue' | 'orange' | 'yellow' }
                          > = {
                            live: { label: 'Live', color: 'green' },
                            scheduled: { label: 'Scheduled', color: 'blue' },
                            posted: { label: 'Posted', color: 'blue' },
                            removed: { label: 'Removed', color: 'orange' },
                            failed: { label: 'Failed', color: 'red' },
                            token_down: { label: 'Token issue', color: 'yellow' },
                            none: { label: 'None', color: 'gray' },
                          };
                          const b = badge[status];
                          return (
                            <Badge variant="soft" color={b.color} title="Facebook post status (checked against Graph)">
                              {b.label}
                            </Badge>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          if ((blog.blogType || 'Post') !== 'Post') {
                            return <Text size="2" className="text-gray-400">-</Text>;
                          }
                          const state = resolveBlogFacebookPublishDateTimeState(blog);
                          if (state.status === 'not_scheduled') {
                            return <Badge variant="soft" color="gray">Not Scheduled</Badge>;
                          }

                          const display = formatFacebookPublishDateTime(state.date);
                          const config: Record<
                            Exclude<typeof state.status, 'not_scheduled'>,
                            { label: string; color: 'green' | 'red' | 'blue' }
                          > = {
                            scheduled: { label: 'Scheduled', color: 'blue' },
                            published: { label: 'Published', color: 'green' },
                            failed: { label: 'Failed', color: 'red' },
                          };
                          const current = config[state.status];

                          return (
                            <Flex direction="column" gap="1">
                              <Badge variant="soft" color={current.color}>
                                {current.label}
                              </Badge>
                              <Text size="1" className={display ? 'text-gray-600' : 'text-gray-400'}>
                                {display || 'Date unavailable'}
                              </Text>
                            </Flex>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell>
                        {(blog.blogType || 'Post') === 'Post' ? (
                          <div className="flex flex-col gap-1">
                            <InstagramStatusBadge status={blog.instagramPostingStatus || 'draft'} />
                            {blog.instagramPostingError?.trim() ? (
                              <span className="max-w-[140px] truncate text-[11px] text-red-600" title={blog.instagramPostingError}>
                                {blog.instagramPostingError}
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <Text size="2" className="text-gray-400">-</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {(blog.blogType || 'Post') === 'Post' ? (
                          (() => {
                            const hasPublishedPost = !!blog.instagramMediaId?.trim() && !blog.instagramDeletedAt;
                            const dateValue =
                              blog.instagramPostingPublishedAt ||
                              blog.instagramPostingScheduledAt ||
                              null;
                            const display = dateValue ? formatFacebookPublishDateTime(new Date(dateValue)) : '';
                            const hasSchedule = !!blog.instagramPostingScheduledAt;
                            const statusMeta: {
                              label: string;
                              color: 'green' | 'blue' | 'gray';
                            } = hasPublishedPost
                              ? { label: 'Published', color: 'green' }
                              : hasSchedule
                                ? { label: 'Scheduled', color: 'blue' }
                                : { label: 'Draft', color: 'gray' };

                            return (
                              <Flex direction="column" gap="1">
                                <Badge variant="soft" color={statusMeta.color}>
                                  {statusMeta.label}
                                </Badge>
                                <Text size="1" className={display ? 'text-gray-600' : 'text-gray-400'}>
                                  {display || 'Not Scheduled'}
                                </Text>
                              </Flex>
                            );
                          })()
                        ) : (
                          <Text size="2" className="text-gray-400">-</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {(blog.blogType || 'Post') === 'Post' ? (
                          <InstagramActionButtons
                            hasPost={!!blog.instagramMediaId?.trim() && !blog.instagramDeletedAt}
                            publishing={blog.instagramPostingStatus === 'publishing'}
                            canDiscardFailure={blog.instagramPostingStatus === 'failed' || !!blog.instagramPostingError?.trim()}
                            discarding={igDiscardLoading && igDiscardTarget?._id === blog._id}
                            onPost={() => openInstagramCompose(blog)}
                            onView={() => openInstagramPost(blog)}
                            onDelete={() => setIgDeleteTarget(blog)}
                            onDiscardFailure={() => setIgDiscardTarget(blog)}
                          />
                        ) : (
                          <Text size="2" className="text-gray-400">-</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" className="text-gray-500">
                          {blog.views ? blog.views.toLocaleString() : '0'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        {(() => {
                          const creator = getCreatorBadge(blog);
                          const isAuthorCreated = creator.label === 'Author Created';

                          // When an Author has requested admin approval, the Admin has assigned
                          // the post to an Editor, or the post is already Published, we hide all
                          // Author actions except "View".
                          const shouldHideAuthorActions =
                            isAuthorRole &&
                            (
                              blog.status === 'Published' ||
                              (
                                workflowStatus &&
                                (
                                  workflowStatus.workflowStatus === 'pending_admin_review' ||
                                  workflowStatus.workflowStatus === 'editor_assigned_pending_review'
                                )
                              )
                            );

                          // Admin/Editor: full actions always.
                          // Author: full actions only when "Author Created" AND no active approval/assignment workflow.
                          const canEditOrDelete =
                            !isAuthorRole ||
                            (isAuthorCreated && !shouldHideAuthorActions);

                          return (
                            <Flex gap="2">
                              {/* View Blog - always visible */}
                              <Link
                                href={`/blogs/${blog.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center justify-center rounded-md border border-blue-200 px-2 py-1.5 text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow min-w-fit cursor-pointer"
                                title="View Blog"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                                  <Eye className="h-3 w-3" />
                                </div>
                              </Link>

                              {(blog.blogType || 'Post') === 'Post' && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void openBlogFacebookSocial(blog);
                                  }}
                                  className="group inline-flex items-center justify-center rounded-md border border-emerald-200 px-2 py-1.5 text-emerald-700 shadow-sm transition-all duration-200 hover:bg-emerald-50 hover:shadow min-w-fit cursor-pointer"
                                  title="Facebook post: view or schedule"
                                >
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 transition-colors">
                                    <Share2 className="h-3 w-3" />
                                  </div>
                                </button>
                              )}

                              {canEditOrDelete && (
                                <>
                                  <Link
                                    href={`/admin/blogs/${blog._id}/edit`}
                                    className="group inline-flex items-center justify-center rounded-md border border-blue-200 px-2 py-1.5 text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow min-w-fit cursor-pointer"
                                    title="Edit Blog"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                                      <Pencil className="h-3 w-3" />
                                    </div>
                                  </Link>
                                  <Link
                                    href={`/admin/faqs?blogSlug=${blog.slug}`}
                                    className="group inline-flex items-center justify-center rounded-md border border-purple-200 px-2 py-1.5 text-purple-700 shadow-sm transition-all duration-200 hover:bg-purple-50 hover:shadow min-w-fit cursor-pointer"
                                    title="Manage FAQs"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-50 text-purple-600 group-hover:bg-purple-100 transition-colors">
                                      <FileQuestion className="h-3 w-3" />
                                    </div>
                                  </Link>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteBlog(blog._id);
                                    }}
                                    disabled={deleting.includes(blog._id)}
                                    className="group inline-flex items-center justify-center rounded-md border border-red-200 px-2 py-1.5 text-red-700 shadow-sm transition-all duration-200 hover:bg-red-50 hover:shadow min-w-fit cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Delete Blog"
                                  >
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-50 text-red-600 group-hover:bg-red-100 transition-colors">
                                      {deleting.includes(blog._id) ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-3 w-3" />
                                      )}
                                    </div>
                                  </button>
                                </>
                              )}
                            </Flex>
                          );
                        })()}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
              </Table.Root>

            {/* Pagination */}
            {totalBlogs > 0 && (
              <Box className="px-6 py-2 bg-gradient-to-r from-blue-50 to-gray-50 rounded-b-md border-t border-gray-100">
                <Flex justify="between" align="center">
                  <Text size="2" className="text-gray-700">
                    Showing <Text weight="medium">{(currentPage - 1) * BLOG_LIST_PAGE_SIZE + 1}</Text> to{' '}
                    <Text weight="medium">{Math.min(currentPage * BLOG_LIST_PAGE_SIZE, totalBlogs)}</Text> of{' '}
                    <Text weight="medium">{totalBlogs}</Text> results
                    {isFiltering && (
                      <span className="ml-2 text-blue-600 text-xs">
                        (filtered)
                      </span>
                    )}
                  </Text>
                  <Flex gap="2">
                    <button
                      className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-2 py-1.5 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={currentPage === 1 || tableBusy}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm font-semibold shadow-sm transition-all duration-200 min-w-[36px] ${currentPage === pageNum
                            ? 'border-blue-300 bg-blue-100 text-blue-800 shadow'
                            : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50 hover:shadow'
                            }`}
                          disabled={tableBusy}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 px-2 py-1.5 text-sm font-semibold text-blue-700 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={currentPage === totalPages || tableBusy}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </Flex>
                </Flex>
              </Box>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Index Toggle Modal */}
      <Dialog.Root open={confirmOpen} onOpenChange={(open) => !confirmLoading && setConfirmOpen(open)}>
        <Dialog.Portal>
          {/* Overlay */}
          <Dialog.Overlay className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm" />

          {/* Content */}
          <Dialog.Content className="fixed z-[1001] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
            <div className={`${confirmLoading ? 'opacity-75 pointer-events-none' : ''} transition-all`}>

              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/70">
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmData.nextIndexed ? 'Enable Indexing' : 'Disable Indexing'}
                </h3>
                <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                  Are you sure you want to {confirmData.nextIndexed ? 'index' : 'no-index'} this blog?
                  This action will update search engine visibility.
                </p>
              </div>

              {/* Blog Info Section */}
              {confirmData.blog && (
                <div className="divide-y divide-gray-100">

                  {/* Thumbnail */}
                  <div className="p-6 bg-gray-50">
                    <Avatar.Root className="block w-full h-44 rounded-lg overflow-hidden border border-gray-200">
                      <Avatar.Image
                        className="w-full h-full object-cover"
                        src={confirmData.blog.featuredImage || '/api/placeholder/400/200'}
                        alt={confirmData.blog.title}
                      />
                      <Avatar.Fallback className="w-full h-full bg-gray-200" />
                    </Avatar.Root>
                  </div>

                  {/* Blog Title & Tags */}
                  <div className="p-6 space-y-3">
                    <div>
                      <h4 className="text-base font-medium text-gray-900 line-clamp-2">
                        {confirmData.blog.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">Unique blog identifier: <span className="text-gray-700 font-medium">{confirmData.blog._id}</span></p>
                    </div>

                    {confirmData.blog.tags?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {confirmData.blog.tags.slice(0, 4).map((t, i) => (
                          <span
                            key={i}
                            className="text-[11px] px-2 py-0.5 rounded-md border border-gray-300 bg-white text-gray-700"
                          >
                            {t}
                          </span>
                        ))}
                        {confirmData.blog.tags.length > 4 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-md border border-gray-300 bg-white text-gray-700">
                            +{confirmData.blog.tags.length - 4}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">No tags assigned</p>
                    )}
                  </div>

                  {/* Meta Info */}
                  <div className="p-6 grid grid-cols-2 gap-x-4 gap-y-3 text-sm text-gray-700 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="truncate">{confirmData.blog.author}</span>
                    </div>

                    <div>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] ${confirmData.blog.status === 'Published'
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : confirmData.blog.status === 'Draft'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
                            : 'bg-blue-50 text-blue-700 border-blue-300'
                          }`}
                      >
                        {confirmData.blog.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>{confirmData.blog.publishDate || 'Not set'}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">Views:</span>
                      <span className="font-medium">{confirmData.blog.views ?? 0}</span>
                    </div>
                  </div>

                  {/* Indexing Status */}
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 font-medium">Current Indexing Status</span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[12px] font-medium ${(confirmData.blog.indexed ?? false)
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-red-50 text-red-700 border-red-300'
                          }`}
                      >
                        {(confirmData.blog.indexed ?? false) ? 'Indexed' : 'No Index'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Buttons */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={confirmLoading}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-200 text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmData.blog && toggleIndex(confirmData.blog._id, confirmData.nextIndexed)}
                  disabled={confirmLoading}
                  className={`px-4 py-2 text-sm font-medium rounded-md text-white transition disabled:opacity-50 ${confirmData.nextIndexed
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                  {confirmData.nextIndexed ? 'Yes, Index' : 'Yes, No-index'}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>

      </Dialog.Root>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          open={confirmDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmDialog(null);
            } else {
              setConfirmDialog({ ...confirmDialog, open: true });
            }
          }}
          title="Move to Trash"
          description={`Are you sure you want to move "${confirmDialog.blogName}" to trash? This blog will be hidden from the public but can be restored later from the trash.`}
          variant="warning"
          confirmText="Yes, Move to Trash"
          onConfirm={confirmDelete}
        />
      )}

      <BlogFacebookPostingModal
        blog={fbComposeBlog}
        open={!!fbComposeBlog}
        onOpenChange={(o) => {
          if (!o) setFbComposeBlog(null);
        }}
        showToast={showToast}
        onPosted={handleFbPosted}
      />
      <SocialMediaViewModal
        post={smViewPost}
        open={smViewOpen}
        onOpenChange={(o) => {
          setSmViewOpen(o);
          if (!o) setSmUnlinkBlogId(null);
        }}
        showAdminToolbar={!!smViewPost && !!smUnlinkBlogId}
        onAdminEdit={() => {
          if (!smViewPost) return;
          setSmViewOpen(false);
          setSmEditPost(smViewPost);
          setSmEditOpen(true);
        }}
        onAdminDelete={() => {
          if (!smViewPost || !smUnlinkBlogId) return;
          setSmDeleteTarget({ row: smViewPost, unlinkBlogId: smUnlinkBlogId });
        }}
      />
      <SocialMediaEditModal
        post={smEditPost}
        open={smEditOpen}
        onOpenChange={setSmEditOpen}
        onSaved={() => {
          void fetchBlogs();
        }}
      />
      {smDeleteTarget && (
        <ConfirmDialog
          open={!!smDeleteTarget}
          onOpenChange={(o) => {
            if (!o) setSmDeleteTarget(null);
          }}
          title="Delete Facebook post"
          description="This removes the post from Facebook and from Social Media Management, and clears the link on this blog."
          variant="danger"
          confirmText="Delete"
          onConfirm={confirmSmDelete}
          loading={smDeleteLoading}
        />
      )}
      <InstagramPostModal
        post={igViewPost}
        open={igViewOpen}
        onOpenChange={(open) => {
          setIgViewOpen(open);
          if (!open) setIgViewPost(null);
        }}
        onDelete={() => {
          const target = blogs.find((blog) => blog.instagramMediaId === igViewPost?.mediaId);
          if (target) setIgDeleteTarget(target);
        }}
      />
      <InstagramPostingModal
        item={igComposeBlog}
        open={igComposeOpen}
        onOpenChange={(open) => {
          setIgComposeOpen(open);
          if (!open) setIgComposeBlog(null);
        }}
        showToast={showToast}
        onSaved={fetchBlogs}
        contentKind="blog"
      />
      <InstagramDeleteDialog
        open={!!igDeleteTarget}
        loading={igDeleteLoading}
        {...(igDeleteTarget?.title ? { title: igDeleteTarget.title } : {})}
        onOpenChange={(open) => {
          if (!open) setIgDeleteTarget(null);
        }}
        onConfirm={confirmInstagramDelete}
      />
      {igDiscardTarget && (
        <ConfirmDialog
          open={!!igDiscardTarget}
          onOpenChange={(open) => {
            if (!open) setIgDiscardTarget(null);
          }}
          title="Discard failed Instagram post"
          description="This clears the failed Instagram publishing state and returns this blog to a draft Instagram-posting state so you can create a new post."
          variant="warning"
          confirmText="Discard failed post"
          onConfirm={confirmInstagramDiscard}
          loading={igDiscardLoading}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </Box>
  );
}
